import { BuildingDTO } from "./building";
import { ClimatisationInfo } from "./climatisation";
import { EnergyPerformanceInfo } from "./energy-performance";
import { FireSecurityUnitInfoDto } from "./fire-security";
import { UnitConformityInfo } from "./unit-conformity-info.dto";
import { UnitInspectionInfoDTO } from "./unit-inspection";

export enum UnitType {
  Residentieel = "Residentieel",
  Commercieel = "Commercieel",
  Overig = "Overig",
}

export enum UnitSubtype {
  Winkel = "Winkel",
  Appartement = "Appartement",
  Studentenkamer = "Studentenkamer",
  Studio = "Studio",
  Kantoor = "Kantoor",
  Duplex = "Duplex",
  Horeca = "Horeca",
  Woning = "Woning",
  Garage = "Garage",
  Fietsenberging = "Fietsenberging",
  Berging = "Berging",
  Buitenstaanplaats = "Buitenstaanplaats",
}

export enum SaleStatus {
  ForSale = "Te koop",
  Sold = "Verkocht",
  NotForSale = "Niet te koop",
}

export enum OutsideSpace {
  GeenBuitenruimte = "Geen Buitenruimte",
  Terras = "Terras",
  Tuin = "Tuin",
}

export enum Orientation {
  N = "N",
  NO = "NO",
  O = "O",
  ZO = "ZO",
  Z = "Z",
  ZW = "ZW",
  W = "W",
  NW = "NW",
}

export enum DigitalConnectionProviders {
  Geen = "Geen",
  Proximus = "Proximus",
  Telenet = "Telenet",
  TelenetEnProximus = "Telenet en Proximus",
}

export enum AvailableStatus {
  Available = "Beschikbaar",
  NotAvailable = "Niet beschikbaar",
  Unknown = "Onbekend",
}

export enum YesNoStatus {
  Yes = "Ja",
  No = "Nee",
  Unknown = "Onbekend",
}

export enum RemoteDoorControlAvailable {
  VIDEO_INTERCOM = "Videofoon",
  ANALOG_ACCESS = "Analoge toegang",
  NO_REMOTE_DOOR_CONTROL = "Geen toegang op afstand mogelijk",
  UNKNOWN = "Onbekend",
}

export enum EPCScore {
  A_PLUS = "A+",
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
  F = "F",
}

/**
 * Current (most recent) EPC for a unit, as enriched by `GET /units/dynamic`
 * with `includeCurrentEpc: true`. Mirrors the energieprestaties derivation.
 */
export type UnitCurrentEpc = {
  label: string;
  kWh: string;
  documentDate: string;
  fileLogId: number | null;
  sharePointFileUrl: string | null;
};

export type UnitDTO = {
  id?: string;
  odooId?: string;
  currentEpc?: UnitCurrentEpc | null;
  /**
   * Start date ("YYYY-MM-DD") of the contract behind the unit's rental status,
   * as enriched by `POST /units/dynamic` with `includeContractStartDate: true`.
   * Null when the unit has no contract or the contract has no start milestone.
   */
  contractStartDate?: string | null;
  boxNumber?: string;
  floor?: number;
  referencePlus?: string;
  climatisationInfo?: ClimatisationInfo;
  unitInspectionInfo?: UnitInspectionInfoDTO;
  unitLayoutDescription?: string;
  building?: BuildingDTO;
  inspectionNumBedrooms?: any;
  inspectionNumBathrooms?: any;
  resourceId?: string;
  inspectionNumToilets?: any;
  inspectionGeneralRemarks?: any;
  buildingId?: string;
  vacantDueToRenovation?: boolean;
  renovationCompletionDate?: string | null;
  unitBuiltAt?: string | Date;
  saleStatus?: SaleStatus;
  visibleInCalendar?: boolean;
  advertisedRentalPrice?: any;
  advertisedProvisions?: any;
  unitMaxCapacityPersons?: any;
  energyPerformanceInfo?: EnergyPerformanceInfo;
  fireSecurityUnitInfo?: FireSecurityUnitInfoDto;
  rentalFiles?: any[];
  rentalVisitTimeslots?: any[];
  unitType?: UnitType;
  unitConformityInfo?: UnitConformityInfo;
  unitSubtype?: UnitSubtype;
  estimatedRentalValue?: number;
  estimatedCommonCosts?: number;
  estimatedPropertyTax?: number;
  grossSurface?: number;
  netSurface?: number;
  contracts?: any[];
  furnished?: boolean;
  outsideSpace?: OutsideSpace;
  outsideSpaceSurface?: number;
  orientationOutsideSpace?: Orientation;
  devices?: any[];
  rooms?: any[];
  digitalConnectionProvidersPossible?: DigitalConnectionProviders;
  description?: string;
  electricityInfo?: any;
  sharePointFolderId?: string;
  sharePointFolderUrl?: string;
  sharePointPicturesFolderId?: string;
  sharePointPicturesFolderUrl?: string;
  rentPublicationLinkImmoweb?: string; // deprecated
  rentIsPublishedOnYallyWebsite?: boolean;
  rentIsPublishedOnBezoekKalender?: boolean;
  rentLinkToVisitationCalendar?: string;
  availableFrom?: string;
  // Immoweb reference fields
  immowebReferenceId?: number;
  immowebReferenceIdAddedTimestamp?: Date | string;
  // Unit commercial/description fields
  unitCommercialDescription?: string;
  wifiAvailable?: AvailableStatus;
  parkingAvailableForRental?: AvailableStatus;
  remoteDoorControlAvailable?: RemoteDoorControlAvailable;
  canBeRentedByCompany?: YesNoStatus;
  smokeDetectorAvailable?: AvailableStatus;
  washingMachineAvailable?: AvailableStatus;
  bathAvailable?: AvailableStatus;
  showerAvailable?: AvailableStatus;
  visitationResponsibleContact?: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone_mobile?: string;
  };
  brokerContacts?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone_mobile?: string;
    company?: { id?: number; name?: string } | null;
  }>;
  /** Manual override for the "leeg sinds" benchmark. ISO date string. */
  vacancyStartDateOverride?: string | null;
  applicationStats?: {
    /** Last-month snapshot used by the home page's vacancy metrics */
    total: number;
    /** All non-archived Applications + Invited for this unit. Matches the
     *  candidates page so the leegstand pill count equals the list length. */
    activeCount?: number;
    interested: number;
    notInterested: number;
    awaitingVisit: number;
    awaitingInterested: number;
    notReacted: number;
  };
};
